import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy } from '@ngneat/until-destroy';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';
import { environment } from '../../../../../../environments/environment';
import { ISmartyAddress } from '@shared/models/address';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import {
  BehaviorSubject,
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  tap,
} from 'rxjs';
import { RecaptchaService } from '@core/services/recaptcha.service';

const SmartyCore = SmartyStreetsSDK.core;
const Lookup = SmartyStreetsSDK.usAutocompletePro.Lookup;

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-sell-verify-address',
  templateUrl: './verify-address.component.html',
  styleUrls: ['./verify-address.component.scss'],
})
export class VerifyAddressComponent {
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;
  @ViewChild('streetAddressInput') streetAddressInput!: ElementRef;
  @Output() selectAddress = new EventEmitter<ISmartyAddress>();
  streetAddress$ = new BehaviorSubject('');
  protected focused = false;
  protected results: ISmartyAddress[] = [];
  protected resultsSelected: ISmartyAddress[] = [];
  private client: any;
  private lookup?: SmartyStreetsSDK.usAutocompletePro.Lookup;
  private checked = false;
  private selected = false;

  constructor(
    private recaptchaV3Service: ReCaptchaV3Service,
    private recaptchaService: RecaptchaService
  ) {
    let key = environment.smartyCode;
    const credentials = new SmartyCore.SharedCredentials(key);
    let clientBuilder = new SmartyCore.ClientBuilder(credentials).withLicenses([
      'us-autocomplete-pro-cloud',
    ]);
    this.client = clientBuilder.buildUsAutocompleteProClient();
    this.streetAddress$
      .pipe(
        distinctUntilChanged(),
        filter((address) => address.length > 3),
        mergeMap((address) => {
          if (!this.checked) {
            return this.needRecaptchaCheck().pipe(
              map((result) => {
                if (!result) {
                  return '';
                }
                this.checked = true;
                return address;
              })
            );
          }
          return of(address);
        }),
        filter((address) => !!address),
        map((address: string) => {
          this.lookup = new Lookup(address);
          this.lookup.maxResults = 10;
          (this.lookup as any).source = 'all';
          console.log('Lookup', this.lookup);
          return this.lookup;
        }),
        mergeMap((lookup) =>
          from(this.client.send(lookup)).pipe(
            map((results: any) => results.result),
            tap((results) => {
              this.results = results as ISmartyAddress[];
              if (results.length > 0) {
                this.menuTrigger.openMenu();
              } else {
                this.menuTrigger.closeMenu();
              }
            }),
            delay(10),
            tap((results) => {
              if (results.length > 0) {
                this.streetAddressInput.nativeElement.focus();
              }
            })
          )
        ),
        catchError((error) => {
          console.log(error);
          return of([]);
        })
      )
      .subscribe();
  }

  get streetAddress(): string {
    return this.streetAddress$.value;
  }

  set streetAddress(value: string) {
    this.streetAddress$.next(value);
  }

  needRecaptchaCheck(): Observable<boolean> {
    return this.recaptchaV3Service
      .execute('getMyOffer')
      .pipe(mergeMap((token) => this.recaptchaService.checkToken(token)));
  }

  async handleSelected(selected: string) {
    if (!this.lookup) return;
    this.lookup.selected = selected;
    try {
      const results = await this.client.send(this.lookup);
      this.resultsSelected = results.result;
    } catch (err) {
      console.log(err);
    }
  }

  handleSelect(address: ISmartyAddress) {
    if (address.entries > 1) {
      const selected =
        address.streetLine +
        ' ' +
        address.secondary +
        ' (' +
        address.entries +
        ') ' +
        address.city +
        ', ' +
        address.state +
        ' ' +
        address.zipcode;
      this.handleSelected(selected);
    } else {
      this.selectAddress.emit(address);
      this.streetAddress =
        address.streetLine +
        ' ' +
        address.secondary +
        ' ' +
        address.city +
        ', ' +
        address.state +
        ' ' +
        address.zipcode;
      setTimeout(() => {
        this.menuTrigger.closeMenu();
      }, 500);
    }
  }

  clearSelected() {
    setTimeout(() => {
      this.resultsSelected = [];
    }, 500);
  }

  handleFocus() {
    this.focused = true;
    if (this.streetAddress.length > 3 && this.results.length > 0) {
      this.menuTrigger.openMenu();
    }
  }
}
